<template>
  <div class="trend-line-chart">
    <!-- 加载状态 -->
    <div v-if="loading" class="chart-loading">
      <i class="el-icon-loading"></i>
      <span>数据加载中...</span>
    </div>

    <!-- 空数据状态 -->
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-data-line"></i>
      <span>暂无数据</span>
    </div>

    <!-- 图表容器 -->
    <div v-else ref="chart" class="chart-container"></div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'trend-line-chart',
  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      resizeHandler: null
    }
  },

  computed: {
    hasData() {
      return this.data && this.data.dates && this.data.dates.length > 0
    }
  },

  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.hasData && !this.chart) {
            this.initChart()
          } else if (this.chart) {
            this.renderChart()
          }
        })
      },
      deep: true
    },

    loading(newVal) {
      if (!newVal && this.hasData) {
        this.$nextTick(() => {
          if (!this.chart) {
            this.initChart()
          } else {
            this.renderChart()
          }
        })
      }
    }
  },

  mounted() {
    this.initChart()
    this.bindEvents()
  },

  beforeDestroy() {
    this.unbindEvents()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },

  methods: {
    initChart() {
      if (!this.$refs.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.renderChart()
    },

    renderChart() {
      // 先检查数据，无数据时销毁图表实例
      if (!this.hasData) {
        if (this.chart) {
          this.chart.dispose()
          this.chart = null
        }
        return
      }

      if (!this.chart) return

      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e8eaec',
          borderWidth: 1,
          textStyle: {
            color: '#1f2d3d'
          }
        },
        legend: {
          data: ['腾讯人脸对比', '百度人脸对比', '腾讯静态活体', '百度静态活体', '旷视动态活体'],
          top: '0%',
          textStyle: {
            color: '#1f2d3d',
            fontSize: 12
          },
          itemWidth: 20,
          itemHeight: 12
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '12%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.data.dates || [],
          axisLabel: {
            color: '#606266',
            fontSize: 11,
            rotate: 45
          },
          axisLine: {
            lineStyle: {
              color: '#e8eaec'
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            color: '#606266',
            fontSize: 11
          },
          splitLine: {
            lineStyle: {
              color: '#e8eaec',
              type: 'dashed'
            }
          }
        },
        series: [
          {
            name: '腾讯人脸对比',
            type: 'line',
            smooth: true,
            data: this.data.tencentFaceData || [],
            lineStyle: {
              width: 2,
              color: '#667eea'
            },
            itemStyle: {
              color: '#667eea'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(102, 126, 234, 0.3)' },
                { offset: 1, color: 'rgba(102, 126, 234, 0.05)' }
              ])
            }
          },
          {
            name: '百度人脸对比',
            type: 'line',
            smooth: true,
            data: this.data.baiduFaceData || [],
            lineStyle: {
              width: 2,
              color: '#5daf7f'
            },
            itemStyle: {
              color: '#5daf7f'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(93, 175, 127, 0.3)' },
                { offset: 1, color: 'rgba(93, 175, 127, 0.05)' }
              ])
            }
          },
          {
            name: '腾讯静态活体',
            type: 'line',
            smooth: true,
            data: this.data.tencentLiveData || [],
            lineStyle: {
              width: 2,
              color: '#4facfe'
            },
            itemStyle: {
              color: '#4facfe'
            }
          },
          {
            name: '百度静态活体',
            type: 'line',
            smooth: true,
            data: this.data.baiduLiveData || [],
            lineStyle: {
              width: 2,
              color: '#fad961'
            },
            itemStyle: {
              color: '#fad961'
            }
          },
          {
            name: '旷视动态活体',
            type: 'line',
            smooth: true,
            data: this.data.megviiActionData || [],
            lineStyle: {
              width: 2,
              color: '#fa7c91'
            },
            itemStyle: {
              color: '#fa7c91'
            }
          }
        ]
      }

      this.chart.setOption(option, true)
    },

    bindEvents() {
      this.resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    unbindEvents() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    }
  }
}
</script>

<style scoped>
.trend-line-chart {
  height: 100%;
  position: relative;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  border-radius: 12px;
  padding: 8px;
}

.chart-container {
  width: 100%;
  height: 380px;
}

.chart-loading,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 380px;
  color: #909399;
  font-size: 14px;
}

.chart-loading i,
.chart-empty i {
  font-size: 48px;
  margin-bottom: 12px;
}
</style>




// WEBPACK FOOTER //
// src/custom/components/stats/face-stats/components/trend-line-chart.vue